import { DateRange }          from 'rewire-common';
import deepequals             from 'fast-deep-equal';
import {
  IEmployeeSummary,
  IResult,
  ProductName,
  ISelectableEntity,
  ScheduleFilterInput,
  FieldFilterInput,
  ISearchResult
}                             from '../DataTypes';
import DataQueries            from '../graphql/Queries';
import {
  payRouteManager,
  IRouteManagerState
}                             from '../main-navigation/RouteManager';
import { isSame }             from '../utility/DateUtils';
import RoutedEntityListModel  from '../ui/RoutedEntityListModel';
import mainPageModel          from '../main-navigation/MainPageModel';

interface ISummaryListState {
  effective?: DateRange;
}

export class SummaryListModel extends RoutedEntityListModel<ISummaryListState> {
  // provide injection opportunities for TimecardModel to provide implementations
  canSave: () => boolean;
  save:    () => void;

  constructor() {
    super();
    this.state.effective = undefined;
  }

  navigateTo(employeeId?: string) {
    payRouteManager.navigateTo({ selectedEmployeeId: employeeId });
  }

  protected getTitle(e?: IEmployeeSummary): string {
    return e ? `Timecard - ${e.name} - ${ProductName}` : `Timecard - ${ProductName}`;
  }

  getEmployee(employeeId: string): IEmployeeSummary | undefined {
    return this.getEntity(employeeId) as IEmployeeSummary | undefined;
  }

  get employees(): IEmployeeSummary[] {
    return this.state.entities as IEmployeeSummary[];
  }

  get effective(): DateRange | undefined {
    return this.state.effective;
  }

  set effective(effective: DateRange | undefined) {
    this.state.effective = effective;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return this.state.selectedEntity as IEmployeeSummary | undefined;
  }

  set selectedEmployee(employee: IEmployeeSummary | undefined) {
    this.state.selectedEntity = employee;
  }

  get employeeCount(): number {
    return this.employees.length;
  }

  get viewOnly(): boolean {
    return mainPageModel.timecardViewOnly;
  }

  reset() {
    super.reset();
    this.effective = undefined;
  }

  shouldMaintainCurrentPage(args: {prevState: IRouteManagerState, currState: IRouteManagerState, effective?: DateRange, route?: string}): boolean {
    // if only the route is changing, maintain the page (so here, if it's different, return true)
    return ((args.prevState.route !== args.route) && !this.shouldLoad(args, true));
  }

  protected shouldLoad(args: {prevState: IRouteManagerState, currState: IRouteManagerState, effective?: DateRange, route?: string}, ignoreRoute?: boolean): boolean {
    if (!isSame(args.effective, args.prevState.effective))             return true;
    if (!(deepequals(args.prevState.filters, args.currState.filters))) return true;
    if (!(deepequals(args.prevState.sorts,   args.currState.sorts)))   return true;
    if (!ignoreRoute) {
      if (args.prevState.route !== args.route) return true;
    }
    return false;
  }

  async _pagingLoad(): Promise<{ data?: IEmployeeSummary[], cursor?: string, totalCount: number }> {
    const { effective, filters, sorts, selectedBatchId } = payRouteManager;

    this.currentPage = this.pagingModel.page;
    this.effective = effective;
      if (!effective) {
      return { cursor: '', totalCount: 0 };
    }

    const employeeSummaryFilters: ScheduleFilterInput = {...filters};
    employeeSummaryFilters.assignedEmployeeIds && delete employeeSummaryFilters.assignedEmployeeIds;
    employeeSummaryFilters.batchIds            && delete employeeSummaryFilters.batchIds;

    if (selectedBatchId) {
      employeeSummaryFilters.batchIds = [selectedBatchId];
    }

    const results = await DataQueries.getEmployeeSummaries(effective, employeeSummaryFilters, sorts, this.pagingModel.itemsPerPage, this.pagingModel.currentPage);
    return results;
  }

  async loadItem(employeeId: string, otherArgs: {effective: DateRange}): Promise<IEmployeeSummary | undefined> {
    const filter:  FieldFilterInput          = { employeeIds: [employeeId] };
    const results: IResult<IEmployeeSummary> = await DataQueries.getEmployeeSummaries(otherArgs.effective, filter);
    return (results?.data?.length && results.data[0]) || undefined;
  }

  async loadSingleEmployee(employeeId: string, effective: DateRange) {
    if (!this.getEmployee(employeeId)) return;
    if (this.effective && !this.effective.contains(effective)) return;

    await this.loadItem(employeeId, {effective});
    // const result: IEmployeeSummary | undefined = await this.loadItem(employeeId, {effective});
    // if (result) {
      // const employee = this.getEmployee(employeeId);
      // if (employee) {
      //   employee.error   = result.error;
      //   employee.warning = result.warning;
      // }
    // }
  }

  protected updateEntities(entities?: ISelectableEntity[]) {
    super.updateEntities(entities);
    timecardModel.updateErrorWarningCountsForEmployees();
  }

  private addEmployeeIfMissing(employee: ISearchResult): IEmployeeSummary {
    let employeeSummary: IEmployeeSummary | undefined = this.getEmployee(employee.id);
    if (!employeeSummary) {
      employeeSummary = {
        id:         employee.id,
        name:       employee.name!,
        warning:    0,
        error:      0,
        info:       0,
        isSelected: false,
        saving:     false
      };
      this.employees.push(employeeSummary);
      this.employees.sort((e1, e2) => e1.name.localeCompare(e2.name));
    }
    employeeSummary = this.getEmployee(employee.id);
    return employeeSummary!;
  }

  async addTimecardForEmployee(employee: ISearchResult) {
    if (this.viewOnly) return;
    const employeeSummary: IEmployeeSummary = this.addEmployeeIfMissing(employee);
    this.navigateTo(employeeSummary.id);
    await this.loadSingleEmployee(employee.id, this.effective!);
  }

  makeLink(employeeId: string): string {
    return payRouteManager.createPayPlanLinkFor({ selectedEmployeeId: employeeId });
  }
}

const summaryListModel = new SummaryListModel();
export default summaryListModel;

import timecardModel from './TimecardModel';